import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import type {IAuthApiProvider} from "data/providers/api/auth.api.provider";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IUser, IUserAvatar, IUserUpdateFields} from "data/types/user";
import {trackSentryErrors} from "data/utils";
import type {IJSONProvider} from "data/providers/json/json.provider";
import {first} from "lodash";
import {AxiosError} from "axios";

export interface IUserStore {
	user: IUser | undefined;
	isAuthorized: boolean;
	get isSessionChecked(): boolean;
	get isTermsChecked(): boolean | null;
	get userUpdateFields(): IUserUpdateFields;
	get defaultAvatar(): IUserAvatar | undefined;
	restoreSession(): Promise<IUser | void>;
	requestUserProfile(): Promise<void>;
	requestUserAvatars(): Promise<void>;
	updateUserProfile(): Promise<IUser | void>;
	setUserUpdateFields(fields: IUserUpdateFields): void;
}

@injectable()
export class UserStore implements IUserStore {
	static notAuthorizedError = "User is not authorized";

	@observable private _isSessionChecked = false;
	@observable private _isTermsChecked: boolean | null = false;

	@observable private _userUpdateFields: IUserUpdateFields = {};

	@observable private _userAvatars: IUserAvatar[] = [];

	constructor(
		@inject(Bindings.AuthApiProvider) private _authApiProvider: IAuthApiProvider,
		@inject(Bindings.JSONProvider) private _jsonProvider: IJSONProvider,
		@inject(Bindings.LocalizationStore) private i18n: ILocalizationStore
	) {
		makeAutoObservable(this);
	}

	@observable private _user?: IUser = undefined;

	get user() {
		return this._user;
	}

	get defaultAvatar() {
		return first(this._userAvatars);
	}

	get isAuthorized() {
		return !!this._user;
	}

	get isSessionChecked() {
		return this._isSessionChecked;
	}

	get isTermsChecked() {
		return this._isTermsChecked;
	}

	get userUpdateFields() {
		return this._userUpdateFields;
	}

	@action
	setUserUpdateFields(fields: IUserUpdateFields) {
		this._userUpdateFields = {
			...this._userUpdateFields,
			...fields,
		};
	}

	@action
	async restoreSession() {
		try {
			const response = await this._authApiProvider.getSession(this.i18n.languageOnly);
			const user = response.data.success.user;
			if (user) {
				runInAction(() => {
					this._user = user;
				});
			}
		} catch (err) {
			const restoreSessionError = err as AxiosError;

			// It's expected. Check on user's session
			if (restoreSessionError.response?.status !== 401) {
				trackSentryErrors(restoreSessionError, {}, "refreshSession");
			}
		} finally {
			runInAction(() => {
				this._isSessionChecked = true;
			});
		}
	}

	@action
	async updateUserProfile() {
		try {
			const response = await this._authApiProvider.updateProfile(
				this.i18n.languageOnly,
				this._userUpdateFields
			);
			const profile = response.data.success.profile;
			if (profile) {
				runInAction(() => {
					this._userUpdateFields = {
						terms: !!profile.terms,
						isNotificationsEnabled: !!profile.isNotificationsEnabled,
						optInGZ: !!profile.optInGZ,
					};

					this._isTermsChecked = profile.terms;
				});
			}
		} catch (restoreSessionError) {
			trackSentryErrors(restoreSessionError, {}, "updateUserProfile");
		}
	}

	@action
	async requestUserProfile() {
		if (!this._user) {
			return;
		}
		try {
			const response = await this._authApiProvider.getProfile(this.i18n.languageOnly);
			const profile = response.data.success.profile;

			if (profile) {
				runInAction(() => {
					this._userUpdateFields = {
						terms: !!profile.terms,
						isNotificationsEnabled:
							!!this._user?.optInGZ || !!profile.isNotificationsEnabled,
						optInGZ: !!profile.optInGZ,
						teamMarketing: !!profile.teamMarketing,
						sponsorMarketing: !!profile.sponsorMarketing,
					};

					this._isTermsChecked = profile.terms;
				});
			}
		} catch (restoreSessionError) {
			trackSentryErrors(restoreSessionError, {}, "updateUserProfile");
		}
	}

	@action
	async requestUserAvatars() {
		if (!this._user) {
			return;
		}
		try {
			const response = await this._jsonProvider.getAvatars();
			runInAction(() => {
				this._userAvatars = response.data.avatars;
			});
		} catch (restoreSessionError) {
			trackSentryErrors(restoreSessionError, {}, "updateUserProfile");
		}
	}
}
